import React, { forwardRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Flex } from '../Rebass';
import { AnimatePresence, motion } from '../Motion';
import { Heading, Button, Text } from '../..';
import VideoModal from '../VideoModal';
import { ProgressContainer, ProgressLine } from '../Hero';
import Pattern, { LAYOUT_TYPES, PatternProps } from '../Pattern';
import useHeroParallax from '../../hooks/useHeroParallax';
import { SxStyleProp } from '../Rebass/types';

interface HeroVideoProps {
  headline?: string;
  subtitle?: string;
  fullUrl?: string;
  previewUrl?: string;
  renderThumbnail?: () => React.ReactNode;
  color?: string;
  showPattern?: boolean;
  patternProps?: PatternProps;
  colorScheme?: string;
  captionSx?: SxStyleProp;
  headlineSx?: SxStyleProp;
  heroType?: string;
  buttonText?: string;
}

const HeroContainer = styled(Box)`
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const BackgroundContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ImagePreview = motion.custom(styled(Box)`
  width: 100%;
  height: 100%;
  z-index: 2;
  will-change: transform;
`);

const HeroOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
`;

const HeroOverlayInner = styled(Box)`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const HeadlineContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const HeroOverlayBottom = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const StyledVideo = motion.custom(styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform;
`);

import { ForwardedRef } from 'react';

const HeroVideo = forwardRef(
  (props: HeroVideoProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      headline,
      subtitle,
      fullUrl,
      previewUrl,
      renderThumbnail,
      color = 'textWhite',
      showPattern,
      patternProps,
      colorScheme,
      captionSx,
      headlineSx,
      heroType,
      buttonText,
    } = props;
    const [isPreviewBuffered, setIsPreviewBuffered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { headlineStyle, backgroundStyle, captionStyle } = useHeroParallax();

    const handleVideoPlay = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsModalOpen(true);
    };

    const playButton = (
      // @ts-ignore
      <motion.div style={captionStyle}>
        <Button
          variant="outline"
          colorScheme="baseWhite"
          icon="play"
          onClick={handleVideoPlay}
          onTouchEnd={handleVideoPlay}
          size="small"
        >
          {buttonText || 'Play Video'}
        </Button>
      </motion.div>
    );

    return (
      <React.Fragment>
        <VideoModal
          isOpen={isModalOpen}
          toggleIsOpen={() => setIsModalOpen(!isModalOpen)}
          videoUrl={fullUrl}
        />
        <HeroContainer ref={ref}>
          <BackgroundContainer>
            {/* @ts-ignore */}
            <AnimatePresence initial={false}>
              {(isModalOpen || !isPreviewBuffered) && (
                <ImagePreview
                  data-testid="preview-image"
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 1 }}
                  // @ts-ignore
                  style={backgroundStyle}
                >
                  {renderThumbnail()}
                </ImagePreview>
              )}
            </AnimatePresence>
            <StyledVideo
              autoPlay={true}
              loop={true}
              muted={true}
              controls={false}
              playsInline={true}
              onCanPlay={() => setIsPreviewBuffered(true)}
              // @ts-ignore
              style={backgroundStyle}
            >
              <source src={previewUrl} type="video/mp4" />
            </StyledVideo>
          </BackgroundContainer>
          <HeroOverlay>
            <HeroOverlayInner>
              <HeadlineContainer heroType={heroType}>
                {/* @ts-ignore */}
                <motion.div
                  style={{
                    ...headlineStyle,
                    willChange: 'transform, opacity, letter-spacing',
                  }}
                >
                  <Heading
                    as="h1"
                    size="hero"
                    color={color}
                    sx={{ ...headlineSx }}
                  >
                    {headline}
                  </Heading>
                </motion.div>
              </HeadlineContainer>
              <HeroOverlayBottom p={[4]}>
                <Box sx={{ flex: ['1 0 20%', '1 0 20%', '1 0 33%'] }}>
                  {fullUrl && (
                    <Box sx={{ display: ['none', 'none', 'block', 'block'] }}>
                      {playButton}
                    </Box>
                  )}
                </Box>
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="flex-end"
                  textAlign="center"
                  width={[4 / 12, 4 / 12, 'auto']}
                  sx={{ flex: ['1 0 60%', '1 0 60%', '1 0 33%'] }}
                >
                  {/* @ts-ignore */}
                  <motion.div style={captionStyle}>
                    <Text
                      size="caption"
                      color={color}
                      textAlign="center"
                      fontFamily={'variable'}
                      sx={{ ...captionSx }}
                    >
                      {subtitle}
                    </Text>
                  </motion.div>
                  {fullUrl && (
                    <Box
                      sx={{ display: ['block', 'block', 'none', 'none'] }}
                      mt={[1]}
                    >
                      {playButton}
                    </Box>
                  )}
                </Flex>
                <Box sx={{ flex: ['1 0 20%', '1 0 20%', '1 0 33%'] }}></Box>
              </HeroOverlayBottom>
              <ProgressContainer>
                <ProgressLine color={colorScheme} />
              </ProgressContainer>
              {showPattern && (
                <Pattern layoutType={LAYOUT_TYPES.HERO} {...patternProps} />
              )}
            </HeroOverlayInner>
          </HeroOverlay>
        </HeroContainer>
      </React.Fragment>
    );
  },
);

export default HeroVideo;
